<script setup>
import emitter from "@/utils/bus"; // 通信对象
import { ref, computed } from "vue";
import closeIcon from "@/assets/icon/close.svg";
import { useConfigStore } from "@/store/config";
import { setLocalStorage, getLocalStorage, removeLocalStorage, setCacheV1, getCacheV1, openExternalLink } from "@/utils/common"
import { useI18n } from "vue-i18n";

import ton from "@/assets/payment-icon/ton.svg";
import stars from "@/assets/payment-icon/stars.svg";
import googlePay from "@/assets/payment-icon/google.svg";
import applePay from "@/assets/payment-icon/apple.svg";
import usdt from "@/assets/payment-icon/usdt.svg";
import tron from "@/assets/crypto_icon/71.png";
import tonusdt from "@/assets/crypto_icon/75.png";
import { getStarsSku, getTonSku, getUsdtSku } from "@/api/TON";


import { useUserStore } from "@/store/user";
import { checkOrderV4 } from "@/api/TON";


const config = useConfigStore();
const { t } = useI18n();
const amount_usdt = ref(0); // 购买金额
const isShow = ref(false);
const isLoading = ref(false);
const loadingItem = ref("");
const paymentStatus = ref(""); // 添加支付状态提示
const userStore = useUserStore();
const type = ref("");
const minerId = ref(0); // 商品 id
const suk_type = ref(0); // 商品类型
const isAndroid = () => /android/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

const paymentOptions = computed(() => {
  const options = [];

  if (isAndroid()) {
    options.push({
      id: "stars",
      name: "Google Pay",
      icon: googlePay,
    });
  } else if (isIOS()) {
    options.push({
      id: "stars",
      name: "Apple Pay",
      icon: applePay,
    });
  } else {
    options.push({
      id: "stars",
      name: "Stars",
      icon: stars,
    });
  }

  options.push({
    id: "ton",
    name: "TON",
    icon: ton,
  });

  options.push({
    id: "TonUSDT",
    name: "USDT",
    network: "TON",
    icon: tonusdt,
  });

  options.push({
    id: "TronUSDT",
    name: "USDT",
    network: "Tron(TRC20)",
    icon: tron,
  });

  return options;
});

const handlePayment = async (item) => {
  if (isLoading.value) return;
  isLoading.value = true;
  loadingItem.value = item.id;
  paymentStatus.value = t("message.creating_order"); // 修正为 t 函数

  try {
    if (!item.network) {
      switch (item.id) {
        case "stars":
          let starsRes = await getStarsSku({
            id: minerId.value,
            suk_type: suk_type.value,
          }, type.value);
          if (starsRes.code === 200) {
            paymentStatus.value = t("message.opening_payment"); // 修正为 t 函数
            // 浏览器环境：直接打开支付链接
            openExternalLink(starsRes.data.url);
            // 浏览器中无法监听支付状态，提示用户支付完成后刷新
            paymentStatus.value = t("message.payment_pending"); // 等待支付
            setTimeout(() => {
              paymentStatus.value = "";
            }, 3000);
          } else {
            paymentStatus.value = t("message.create_order_failed"); // 修正为 t 函数
            setTimeout(() => {
              paymentStatus.value = "";
            }, 2000);
          }
          break;
        case "ton":
          paymentStatus.value = t("message.creating_ton_order"); // 修正为 t 函数
          let tonRes;
          let cache = getCacheV1(`TON${minerId.value}`);
          if (cache) {
            let cacheData = JSON.parse(cache);
            let res = await checkOrderV4({ order_id: cacheData.data.order_id })
            if (res.data === "success") {
              removeLocalStorage(`TON${minerId.value}`);
              tonRes = await getTonSku({
                id: minerId.value,
                suk_type: suk_type.value,
              }, type.value);
            } else {
              tonRes = cacheData;
            }
          } else {
            tonRes = await getTonSku({
              id: minerId.value,
              suk_type: suk_type.value,
            }, type.value);
          }

          if (tonRes.code === 200) {
            setCacheV1(`TON${minerId.value}`, JSON.stringify(tonRes), 10);
            emitter.emit("showTonPayment", {
              data: tonRes,
              isBuy: false,
              params: {
                amount_usdt: amount_usdt.value
              },
              isMode: false,
            });
            paymentStatus.value = "";
          } else {
            paymentStatus.value = t("message.create_order_failed"); // 修正为 t 函数
            setTimeout(() => {
              paymentStatus.value = "";
            }, 2000);
          }
          break;
      }
    } else {
      paymentStatus.value = t("message.creating_usdt_order"); // 修正为 t 函数
      let usdtRes;
      let cache = getCacheV1(`${item.id}+${minerId.value}`);
      if (cache) {
        let cacheData = JSON.parse(cache);
        let res = await checkOrderV4({ order_id: cacheData.data.order_id })
        if (res.data === "success") {
          removeLocalStorage(`${item.id}+${minerId.value}`);
          switch (item.id) {
            case "TonUSDT":
              usdtRes = await getUsdtSku({
                id: minerId.value,
                chain_type: "ton",
                suk_type: suk_type.value
              }, type.value)
              break;
            case "TronUSDT":
              usdtRes = await getUsdtSku({
                id: minerId.value,
                chain_type: "tron",
                suk_type: suk_type.value
              }, type.value)
              break;
          }
        } else {
          usdtRes = cacheData;
        }
      } else {
        switch (item.id) {
          case "TonUSDT":
            usdtRes = await getUsdtSku({
              id: minerId.value,
              chain_type: "ton",
              suk_type: suk_type.value
            }, type.value)
            break;
          case "TronUSDT":
            usdtRes = await getUsdtSku({
              id: minerId.value,
              chain_type: "tron",
              suk_type: suk_type.value
            }, type.value)
            break;
        }
      }

      if (usdtRes.code === 200) {
        setCacheV1(`${item.id}+${minerId.value}`, JSON.stringify(usdtRes), 10);
        emitter.emit("showOtherTonPayment", {
          data: usdtRes,
          amount: amount_usdt.value,
          params: {
            amount_usdt: amount_usdt.value,
            chain_type: item.id === "TonUSDT" ? "ton" : "tron"
          },
          selectedNetwork: item.id === "TonUSDT" ? "ton" : "tron",
          isMode: false,
        });
        paymentStatus.value = "";
      } else {
        paymentStatus.value = t("message.create_order_failed"); // 修正为 t 函数
        setTimeout(() => {
          paymentStatus.value = "";
        }, 2000);
      }
    }
  } catch (error) {
    console.log(error);
    paymentStatus.value = t("message.error"); // 修正为 t 函数
    setTimeout(() => {
      paymentStatus.value = "";
    }, 2000);
  } finally {
    isLoading.value = false;
    loadingItem.value = "";
  }
};

const selectPayment = (data) => {
  isShow.value = true;
  amount_usdt.value = data.amount_usdt;
  console.log("data", data);
  minerId.value = data.params.id;
  suk_type.value = data.params.suk_type;
  if (data.params.type) {
    type.value = data.params.type;
  } else {
    type.value = "";
  }
};

emitter.on("selectPayment", selectPayment);

</script>

<template>
  <Transition name="payment-modal">
    <div class="viewSelectPayment" v-show="isShow">
      <div class="language">
        <div class="top">
          <div class="top_title">{{ $t("message.select_payment_method") }}</div>
          <!-- <div class="top_introduced">选择支付方式</div> -->
          <div class="close" @click="isShow = false">
            <img :src="closeIcon" alt="" />
          </div>
        </div>
        <div class="list">
          <div v-if="isLoading" class="list-loading">
            <div class="loading-mask"></div>
            <div class="loading-spinner">
              <div class="spinner"></div>
            </div>
          </div>
          <div class="item" v-for="(item, index) in paymentOptions" :key="index" @click="handlePayment(item)">
            <img class="img" :src="item.icon" />
            <div class="topBox">
              <div class="title">{{ item.name }}</div>
              <!-- 网络 -->
              <div class="network" v-if="item.network">{{ item.network }}</div>
            </div>
          </div>
        </div>
      </div>
      <!-- 添加支付状态提示 -->
      <Transition name="fade">
        <div v-if="paymentStatus" class="payment-status-overlay">
          <div class="payment-status-content">
            <div v-if="isLoading" class="loading-spinner"></div>
            <span>{{ paymentStatus }}</span>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.payment-modal-enter-active,
.payment-modal-leave-active {
  transition: all 0.3s ease;
}

.payment-modal-enter-from,
.payment-modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.payment-modal-enter-to,
.payment-modal-leave-from {
  opacity: 1;
  transform: scale(1);
}

.viewSelectPayment {
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  backdrop-filter: blur(5px);
  font-family: Arial, sans-serif;

  .language {
    width: 92vw;
    max-width: 600px;
    margin: 0 auto;
    background: #ffffff;
    display: block;
    padding: 4vw;
    border-radius: 4vw;
    box-shadow: 0 0.6vw 3vw rgba(0, 0, 0, 0.1);

    .button {
      width: 100%;
      height: 8vh;
      border-radius: 2vw;
      margin-top: 10px;
      text-align: center;
      line-height: 8vh;
      font-weight: bold;
      font-size: 5vw;
      color: #000000;
      background-color: var(--theme-color);
      transition: all 0.3s ease;

      &:active {
        opacity: 0.9;
        transform: translateY(-0.2vw);
      }
    }

    .list {
      margin-top: 4vw;
      overflow-y: auto;
      flex-wrap: wrap;
      padding: 0 2px;
      display: flex;
      justify-content: space-between;
      max-height: 38vh;
      position: relative;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      .list-loading {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;

        .loading-mask {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(2px);
        }

        .loading-spinner {
          position: relative;
          width: 10vw;
          height: 10vw;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;

          .spinner {
            width: 8vw;
            height: 8vw;
            border: 0.8vw solid #e5e5e5;
            border-radius: 50%;
            border-top-color: var(--theme-color);
            animation: spin 0.8s linear infinite;
          }
        }
      }

      .item {
        display: flex;
        padding: 3vw;
        box-sizing: border-box;
        height: 8vh;
        border-radius: 2vw;
        margin: 2vw 0;
        align-items: center;
        background: #f5f5f5;
        color: #333333;
        font-size: 4.2vw;
        width: 48%;
        cursor: pointer;
        transition: all 0.3s ease;
        border: 0.3vw solid transparent;
        position: relative;
        overflow: hidden;

        &:hover {
          background: #ffffff;
          transform: translateY(-0.2vw);
          border-color: var(--theme-color);
          box-shadow: 0 0 2vw rgba(0, 255, 157, 0.2);
        }

        .topBox {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 100%;

          .title {
            font-size: 4.2vw;
            color: #333333;
            font-weight: 500;
          }

          .network {
            font-size: 3.5vw;
            color: #666666;
          }
        }

        &>.img {
          width: 5vw;
          height: 5vw;
          margin-right: 3vw;
          background-size: contain;
          background-position: 50%;
          background-repeat: no-repeat;
          flex-shrink: 0;
        }
      }
    }

    .top {
      position: relative;
      padding: 4vw;
      border-bottom: 0.3vw solid #e5e5e5;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .close {
        width: 5vw;
        height: 5vw;
        cursor: pointer;
        transition: all 0.3s ease;

        img {
          width: 100%;
          height: 100%;
          filter: brightness(0) invert(0.6);
        }

        &:hover img {
          filter: brightness(0) invert(0.3);
        }
      }

      &_title {
        font-weight: bold;
        font-size: 5.5vw;
        color: #333333;
      }

      &_introduced {
        color: #666666;
        font-size: 4vw;
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// 添加支付状态提示样式
.payment-status-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3vw;
}

.payment-status-content {
  text-align: center;
  color: #333333;
  font-size: 4vw;
}

.loading-spinner {
  width: 10vw;
  height: 10vw;
  border: 0.8vw solid #e5e5e5;
  border-top-color: var(--theme-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin: 0 auto 3vw;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
